import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import GoogleSignInButton from "@/components/GoogleSignInButton";

const nav = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/rebirthing", label: "Rebirthing Genesis" },
  { to: "/retreats", label: "Retreats" },
  { to: "/dubbi", label: "Dubbi EcoVillage" },
  { to: "/culture", label: "Culture & Arts" },
  { to: "/volunteer", label: "Volunteer" },
  { to: "/contact", label: "Contact" },
  { to: "/store", label: "Store" },
];

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const { user } = useAuth();
  React.useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-bone">
      <header className="sticky top-0 z-50 backdrop-blur-xl backdrop-saturate-150 bg-bone/70 border-b border-forest/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
            <div className="w-9 h-9 rounded-full bg-forest text-bone grid place-items-center font-serif text-lg">C</div>
            <div className="leading-tight">
              <div className="font-serif text-base lg:text-lg text-ink whitespace-nowrap">Celestial Seed Sanctuary</div>
              <div className="overline whitespace-nowrap text-[10px]">Powered by NEOS</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
                className={({ isActive }) => `text-sm transition-colors duration-300 edlink whitespace-nowrap ${isActive ? "text-forest font-medium" : "text-ink-soft hover:text-forest"}`}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/donate" data-testid="nav-donate" className="pill pill-accent text-sm">Donate</Link>
            {user && user.provider === "google" ? (
              <Link to="/account" data-testid="nav-account" className="flex items-center gap-2 text-sm text-forest">
                {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" /> : <User className="h-4 w-4" />}
                Account
              </Link>
            ) : (
              <GoogleSignInButton testId="nav-google-signin">Sign in</GoogleSignInButton>
            )}
          </nav>
          <button aria-label="Menu" onClick={() => setOpen(!open)} className="lg:hidden p-2" data-testid="nav-menu-toggle">
            {open ? <X className="h-6 w-6 text-ink" /> : <Menu className="h-6 w-6 text-ink" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-line bg-bone">
            <div className="px-6 py-6 flex flex-col gap-4">
              {nav.map((n) => (
                <NavLink key={n.to} to={n.to} data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
                  className={({ isActive }) => `text-base ${isActive ? "text-forest font-medium" : "text-ink-soft"}`}>{n.label}</NavLink>
              ))}
              <Link to="/donate" data-testid="nav-mobile-donate" className="pill pill-accent text-sm w-fit">Donate</Link>
              {user && user.provider === "google" ? (
                <Link to="/account" data-testid="nav-mobile-account" className="text-sm text-forest">My Account</Link>
              ) : (
                <GoogleSignInButton testId="nav-mobile-google-signin" className="w-fit">Sign in with Google</GoogleSignInButton>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-line bg-surface mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="font-serif text-2xl text-ink mb-3">Celestial Seed Sanctuary</div>
            <div className="overline mb-4">Powered by NEOS — New Earth Operating System</div>
            <p className="text-ink-soft text-sm leading-relaxed max-w-md">
              A living sanctuary, a practical school, and a regenerative institution — home of the Tribes of Humanity Global Alliance, Rebirthing Genesis, Dubbi EcoVillage, the School of Practical Wisdom &amp; Life, and Culture &amp; Arts.
            </p>
          </div>
          <div>
            <div className="overline mb-3">Explore</div>
            <ul className="space-y-2 text-sm text-ink-soft">
              <li><Link to="/about" className="hover:text-forest edlink">About</Link></li>
              <li><Link to="/retreats" className="hover:text-forest edlink">Retreats</Link></li>
              <li><Link to="/culture" className="hover:text-forest edlink">Culture &amp; Arts</Link></li>
              <li><Link to="/charter" className="hover:text-forest edlink">Institutional Charter</Link></li>
              <li><Link to="/volunteer" className="hover:text-forest edlink">Volunteer</Link></li>
              <li><Link to="/store" className="hover:text-forest edlink">Store</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-3">Support</div>
            <ul className="space-y-2 text-sm text-ink-soft">
              <li><Link to="/donate" className="hover:text-terracotta edlink">Donate</Link></li>
              <li><Link to="/contact" className="hover:text-forest edlink">Contact</Link></li>
              <li><a href="https://www.instagram.com/celestialseedcolombia2026/" target="_blank" rel="noopener noreferrer" className="hover:text-forest edlink">Instagram</a></li>
              <li><Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-forest edlink">Admin Portal</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-line py-6 text-center overline">© {new Date().getFullYear()} NEOS · Rooted in Colombia · Growing worldwide</div>
      </footer>
    </div>
  );
}
